import { Injectable } from "@angular/core";
import moment from "moment";
import { IContentControlRepository } from "../../domain/content-control/repository";
import {
  ScheduleAiJobResponse,
  ScheduleAiJobsRequest,
} from "../../domain/content-control/ai";
import { CensorshipReportEntry } from "../../domain/content-control/reporting";
import {
  ContentControlItem,
  ContentControlItemLinkedFile,
} from "../../domain/content-control/item";
import { ContentControlledItemsSpecifications } from "../../domain/content-control/specifications";
import { HttpService } from "../http/http";
import { ContentControlledEditingDirective } from "../../domain/content-control/editing-directive";
import {
  ContentControlledReportableEvent,
  ContentControlledReportableEventBy,
} from "../../domain/content-control/reportable-event";
import { ContentControlledKeyframe } from "../../domain/content-control/keyframe";
import { ContentControlSubtitlesContainer } from "../../domain/content-control/subtitles";
import {
  ContentControlledLinkedFileMediaTypeEnum,
  ContentControlledMediaTypeEnum,
} from "../../domain/content-control/enums";
import {
  ContentControlledLiveSettings,
  ContentControlledVODSettings,
  IContentControlledItemSettings,
} from "../../domain/content-control/settings";
import { FormatNumber } from "../../domain/common/ui-utils";
import { TenantSettings } from "../../domain/tenant-settings";

@Injectable()
export class ContentControlRepositoryImpl implements IContentControlRepository {
  readonly controller = "content-control-vod";

  constructor(private httpService: HttpService) {}

  async scheduleAiJob(
    request: ScheduleAiJobsRequest,
  ): Promise<ScheduleAiJobResponse[]> {
    const result = [];
    try {
      const response = await this.httpService.post(
        `${this.controller}/schedule-ai-job`,
        request,
      );
      response?.forEach((r) => {
        result.push(
          new ScheduleAiJobResponse(r.JobUid, r.Response, r.IsSuccess),
        );
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
    return result;
  }
  loadSubtitle(url: string): Promise<string> {
    var options = this.httpService.createDefaultNonAuthRequestOption();
    return this.httpService.getJSON(url, options);
  }

  create(mediaId: number): Promise<ContentControlItem> {
    throw new Error("Method not implemented.");
  }
  async searchItems(
    specification: ContentControlledItemsSpecifications,
  ): Promise<ContentControlItem[]> {
    const url =
      `${this.controller}/get-items?SearchQuery=${specification.term}` +
      `&Limit=${specification.limit}&Offset=${specification.offset}` +
      `&DateFrom=${specification.dateFrom ? specification.dateFrom : ""}`;

    var response = await this.httpService.getJSON(url);
    const result: ContentControlItem[] = [];
    response.message.forEach((item) => {
      result.push(this.mapGetItemResponse(item));
    });
    return Promise.resolve(result);
  }
  async getCensorshipAiReport(
    mediaUniqueIdPattern: string,
    from: moment.Moment,
    to: moment.Moment,
  ): Promise<CensorshipReportEntry[]> {
    const url = `Reports/GetCensorshipAiReport?MediaUniqueIdPattern=${mediaUniqueIdPattern}&FromTime=${from.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")}&ToTime=${to.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")}`;
    let result = [];

    let response = await this.httpService.get(url);

    result = this._mapGetCensorshipAiResponse(response);

    console.log(
      `content-control-repository->getCensorshipAiReport->result:`,
      result,
    );
    return Promise.resolve(result);
  }
  async getItemByMediaId(
    mediaId: number,
    includeThumbnails: boolean,
  ): Promise<ContentControlItem> {
    try {
      var result = await this.httpService.getJSON(
        `${this.controller}/get-media-item-details/${mediaId}?includeThumbnails=${includeThumbnails}`,
      );
      if (result.success) {
        return this.mapGetItemResponse(result.message);
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }
  async getItemByChannelUid(channelUid: string): Promise<ContentControlItem> {
    try {
      if (channelUid === "b41c0cb9-350b-4129-a2ad-77e5219ebc44") {
        return FAKE_CHANNEL_ITEM_1;
      }
      return FAKE_CHANNEL_ITEM_2;
    } catch (e) {
      return null;
    }
  }
  async save(item: ContentControlItem): Promise<boolean> {
    return await this.httpService.post(`${this.controller}/save-item`, item);
  }

  async saveLive(item: ContentControlItem): Promise<boolean> {
    const payload = {
      uid: item.uid,
      channelId: item.mediaId,
      editingDirectives: item.editingDirectives,
      itemSettings: item.itemSettings,
    };
    return await this.httpService.post(
      "content-control-live/save-item",
      payload,
    );
  }

  async associateTranscodingJob(
    contentControlItemUid: string,
    transcodingJobUid: string,
  ): Promise<boolean> {
    return await this.httpService.post(
      `${this.controller}/associate-transcoding-job`,
      {
        ContentControlItemUid: contentControlItemUid,
        MediaTranscodingJobUid: transcodingJobUid,
      },
    );
  }
  private fixTimecode(timecode: number, fps: number) {
    if (fps == 0) return timecode;
    timecode /= 1000;
    let fn = timecode * fps;
    let nextFrame = Math.floor(fn);
    if (+(fn - nextFrame).toPrecision(12) >= 1 / fps) {
      nextFrame++;
    }
    return FormatNumber((nextFrame / fps) * 1000);
  }
  private mapReportableEvents(
    responseObject: any,
    mediaFps: number,
  ): ContentControlledReportableEvent[] {
    let response: ContentControlledReportableEvent[] = [];
    if (responseObject && responseObject["response"]) {
      const higestId = 100000;
      responseObject["response"].forEach((aiResponse) => {
        if (aiResponse["type"] === "object_detection") {
          for (let foundClass of aiResponse["classes_found"]) {
            for (let detectionResult of aiResponse["detection_results"][
              foundClass
            ]) {
              const keyframes = detectionResult["keyframes"].map((kf) => {
                const tlwh = kf["tlwh"];
                return new ContentControlledKeyframe(
                  kf["kf_ms"],
                  tlwh[0] * 100,
                  tlwh[1] * 100,
                  tlwh[2] * 100,
                  tlwh[3] * 100,
                );
              });
              let startsAt = 0;
              let endsAt = 0;
              if (
                detectionResult["frames_ms"] &&
                detectionResult["frames_ms"].length > 0
              ) {
                startsAt = this.fixTimecode(
                  detectionResult["frames_ms"][0],
                  mediaFps,
                );
                endsAt = this.fixTimecode(
                  detectionResult["frames_ms"][1],
                  mediaFps,
                );
              }

              response.push(
                new ContentControlledReportableEvent(
                  detectionResult["obj_id"] + higestId,
                  aiResponse["type"],
                  foundClass,
                  ContentControlledReportableEventBy.AI,
                  ContentControlledMediaTypeEnum.Video,
                  foundClass,
                  startsAt,
                  endsAt,
                  keyframes,
                  detectionResult["thumbnail_first"],
                  detectionResult["thumbnail_last"],
                ),
              );
            }
          }
        }
      });
    }

    return response;
  }
  private mapEditingDirectives(
    responseObject: any,
    mediaFps: number,
  ): ContentControlledEditingDirective[] {
    let result: ContentControlledEditingDirective[] = [];
    if (responseObject && Array.isArray(responseObject)) {
      result = responseObject.map((x) => {
        const keyframes = x["keyframes"].map((kf) => {
          return new ContentControlledKeyframe(
            this.fixTimecode(kf.timeInMs, mediaFps),
            kf.x,
            kf.y,
            kf.width,
            kf.height,
          );
        });
        let subtitlesContainer = undefined;
        if (x["subtitles"]) {
          subtitlesContainer = new ContentControlSubtitlesContainer(
            x["subtitles"]["segmentsMap"],
          );
        }
        return new ContentControlledEditingDirective(
          x.id,
          x.mediaType,
          this.fixTimecode(x.startsAtMs, mediaFps),
          this.fixTimecode(x.endsAtMs, mediaFps),
          x.title,
          x.isApproved,
          x.isMitigated,
          x.action,
          keyframes,
          x.startFrame,
          x.endFrame,
          x.instructions,
          subtitlesContainer,
          x.createdAt,
          x.mediaId ?? x.MediaId,
        );
      });
    }

    return result;
  }
  private _mapGetCensorshipAiResponse(
    responseObject: any,
  ): CensorshipReportEntry[] {
    let result: CensorshipReportEntry[] = [];
    if (responseObject && Array.isArray(responseObject)) {
      result = responseObject.map((x) => {
        return new CensorshipReportEntry(
          x.MediaId,
          x.TenantMediaUniqueId,
          moment.utc(x.LastJobTime),
          this.mapReportableEvents(x["ReportableEvents"], x.Fps),
          x.Fps,
          this.mapEditingDirectives(x["EditingDirectives"], x.Fps),
        );
      });
    }

    return result;
  }
  private mapGetItemResponse(
    getItemResponse,
    ts?: TenantSettings,
  ): ContentControlItem {
    const associatedMedia = getItemResponse["media"];
    const contentControlItem = getItemResponse["controlItem"];
    const fps = associatedMedia["FpsRate"] || 0;
    const eds: ContentControlledEditingDirective[] =
      this.mapEditingDirectives(contentControlItem["editedDirectives"], fps) ||
      [];

    const reportableEventsObjects: ContentControlledReportableEvent[] =
      this.mapReportableEvents(contentControlItem["reportableEvents"], fps) ||
      [];

    const settings = this.parseContentControlledItemSettings(
      contentControlItem["aiDetectionSettings"],
    );

    const ccItem = new ContentControlItem(
      contentControlItem["uid"],
      associatedMedia["Id"],
      associatedMedia["CustomId"],
      associatedMedia["Name"],
      associatedMedia["Thumbnail"],
      associatedMedia["PreviewUrl"],
      associatedMedia["TranscodedUrl"],
      associatedMedia["FpsRate"],
      contentControlItem["mediaTranscodingJobUid"],
      !!contentControlItem["reportableEvents"],
      !!contentControlItem["editedDirectives"],
      contentControlItem["aiJobUid"],
      moment.utc(associatedMedia["UploadedTime"]),
      settings,
      reportableEventsObjects,
      eds,
      [],
      getItemResponse.hasAiJobsRunning,
      associatedMedia.MediaMetaData?.MediaStartTimeStampMs || 0,
    );

    if (associatedMedia["Subtitles"]) {
      associatedMedia["Subtitles"].forEach((s) => {
        ccItem.linkedFiles.push(
          new ContentControlItemLinkedFile(
            ContentControlledLinkedFileMediaTypeEnum.Subtitle,
            s.LanguageCode,
            s.Url,
            s.FileName,
            s.Fps,
            s.Offset,
            s.PlaybackSpeed,
          ),
        );
      });
    }

    if (associatedMedia["AudioTracks"]) {
      associatedMedia["AudioTracks"].forEach((a) => {
        ccItem.linkedFiles.push(
          new ContentControlItemLinkedFile(
            ContentControlledLinkedFileMediaTypeEnum.Audio,
            a.LanguageCode,
            a.PreviewUrl,
            a.MediaName,
            associatedMedia.FpsRate,
            0,
            1.0,
          ),
        );
      });
    }

    return ccItem;

    /*if (
      getItemResponse["mediaLinkedFiles"] &&
      Array.isArray(getItemResponse["mediaLinkedFiles"]) &&
      getItemResponse["mediaLinkedFiles"].length > 0
    ) {
      getItemResponse["mediaLinkedFiles"].forEach((lf) => {
        ccItem.linkedFiles.push(
          new ContentControlItemLinkedFile(
            lf["fileType"],
            lf["language"],
            lf["url"],
            lf["name"],
            lf["fps"],
            lf["offset"],
          ),
        );
      });
    }
    return ccItem;*/
  }
  parseReportableEvents(
    contentControlItem: any,
    fps: any,
  ): ContentControlledReportableEvent[] {
    let res: ContentControlledReportableEvent[] = [];
    if (
      contentControlItem["reportableEvents"] &&
      contentControlItem["reportableEvents"]["response"]
    ) {
      const higestId = 100000;
      contentControlItem["reportableEvents"]["response"].forEach(
        (aiResponse) => {
          if (aiResponse["type"] === "object_detection") {
            for (let foundClass of aiResponse["classes_found"]) {
              for (let detectionResult of aiResponse["detection_results"][
                foundClass
              ]) {
                const keyframes = detectionResult["keyframes"].map((kf) => {
                  const tlwh = kf["tlwh"];
                  return new ContentControlledKeyframe(
                    kf["kf_ms"],
                    tlwh[0] * 100,
                    tlwh[1] * 100,
                    tlwh[2] * 100,
                    tlwh[3] * 100,
                  );
                });
                let startsAt = 0;
                let endsAt = 0;
                if (
                  detectionResult["frames_ms"] &&
                  detectionResult["frames_ms"].length > 0
                ) {
                  startsAt = this.fixTimecode(
                    detectionResult["frames_ms"][0],
                    fps,
                  );
                  endsAt = this.fixTimecode(
                    detectionResult["frames_ms"][1],
                    fps,
                  );
                }

                res.push(
                  new ContentControlledReportableEvent(
                    detectionResult["obj_id"] + higestId,
                    aiResponse["type"],
                    foundClass,
                    ContentControlledReportableEventBy.AI,
                    ContentControlledMediaTypeEnum.Video,
                    foundClass,
                    startsAt,
                    endsAt,
                    keyframes,
                    detectionResult["thumbnail_first"],
                    detectionResult["thumbnail_last"],
                  ),
                );
              }
            }
          }
        },
      );
    }
    return res;
  }
  private mapGetLiveItemResponse(getItemResponse): ContentControlItem {
    const associatedChannel = getItemResponse["channel"];
    const contentControlItem = getItemResponse["controlItem"];
    const fps = associatedChannel["FpsRate"] || 0;
    let eds = this.parseEditingDirectives(contentControlItem, fps);
    let res = this.parseReportableEvents(contentControlItem, fps);
    const settings = this.parseContentControlledItemSettings(
      contentControlItem["aiDetectionSettings"],
    );
    const item = new ContentControlItem(
      contentControlItem["uid"],
      associatedChannel["Id"],
      associatedChannel["Uid"],
      associatedChannel["Name"],
      associatedChannel["Thumbnail"],
      associatedChannel["PreviewLink"],
      associatedChannel["AutoLink"],
      fps,
      null,
      true,
      true,
      contentControlItem["aiJobUid"],
      moment.utc(),
      settings,
      res,
      eds,
      [],
      false,
      0,
    );
    return item;
  }
  private parseContentControlledItemSettings(
    json: any,
  ): IContentControlledItemSettings {
    return new ContentControlledVODSettings({
      scalingFactor: json.ScalingFactor || undefined,
    });

    /*if (typeof json !== "object" || json === null) {
      throw new Error("Invalid JSON: expected an object");
    }

    if (json.discriminator !== "VOD" && json.discriminator !== "LIVE") {
      throw new Error('Invalid discriminator: must be either "VOD" or "LIVE"');
    }

    if (json.discriminator === "VOD") {
      return new ContentControlledVODSettings({
        scalingFactor: json.aiDetections?.scalingFactor || undefined,
      });
    } else {
      // Implement LIVE settings here if needed
      throw new Error("LIVE settings not implemented");
    }
     */
  }
  private parseEditingDirectives(
    contentControlItem: any,
    fps: number,
  ): ContentControlledEditingDirective[] {
    let eds: ContentControlledEditingDirective[] = [];
    if (
      contentControlItem["editedDirectives"] &&
      Array.isArray(contentControlItem["editedDirectives"])
    ) {
      eds = contentControlItem["editedDirectives"].map((x) => {
        const keyframes = x["keyframes"].map((kf) => {
          return new ContentControlledKeyframe(
            this.fixTimecode(kf.timeInMs, fps),
            kf.x,
            kf.y,
            kf.width,
            kf.height,
          );
        });
        let subtitlesContainer = undefined;
        if (x["subtitles"]) {
          subtitlesContainer = new ContentControlSubtitlesContainer(
            x["subtitles"]["segmentsMap"],
          );
        }
        return new ContentControlledEditingDirective(
          x.id,
          x.mediaType,
          this.fixTimecode(x.startsAtMs, fps),
          this.fixTimecode(x.endsAtMs, fps),
          x.title,
          x.isApproved,
          x.isMitigated,
          x.action,
          keyframes,
          x.startFrame,
          x.endFrame,
          x.instructions,
          subtitlesContainer,
          x.createdAt,
          x.mediaId ?? x.MediaId,
        );
      });
    }
    return eds;
  }
}
const FAKE_CHANNEL_ITEM_1 = new ContentControlItem(
  "024da7b4-5565-44b3-b815-be7e5529611d",
  1000000100,
  "b41c0cb9-350b-4129-a2ad-77e5219ebc44",
  "HLS-TEST",
  "https://storage.gra.cloud.ovh.net/v1/AUTH_1d68fd5490ae43fcb061e108a8e0e41a/icpublicuploads/be35bafc-b548-ea11-9132-ac1f6b712c7b/9e9bc408-7203-4483-b1e7-c19696aea817.png",
  "https://oci-fs2-edge.evrideo.com:8443/b41c0cb9-350b-4129-a2ad-77e5219ebc44_1000027186_HLS/manifest.m3u8",
  "https://oci-fs2-edge.evrideo.com:8443/b41c0cb9-350b-4129-a2ad-77e5219ebc44_1000027186_HLS/manifest.m3u8",
  25,
  "",
  true,
  true,
  "",
  moment.utc(),
  new ContentControlledLiveSettings(
    { scalingFactor: 0 },
    5 * 1000 * 60,
    30 * 1000 * 60,
  ),
  [],
  [],
  [],
  false,
);

const FAKE_CHANNEL_ITEM_2 = new ContentControlItem(
  "B7079B06-7AA2-4036-9F83-4DC20024F0A3",
  1000000058,
  "B7079B06-7AA2-4036-9F83-4DC20024F0A3",
  "MOVIES2",
  "https://storage.gra.cloud.ovh.net/v1/AUTH_1d68fd5490ae43fcb061e108a8e0e41a/icpublicuploads/be35bafc-b548-ea11-9132-ac1f6b712c7b/9e9bc408-7203-4483-b1e7-c19696aea817.png",
  "https://oci-fs2.evrideo.com:8443/b7079b06-7aa2-4036-9f83-4dc20024f0a3_1000026065_HLS/manifest.m3u8",
  "https://oci-fs2.evrideo.com:8443/b7079b06-7aa2-4036-9f83-4dc20024f0a3_1000026065_HLS/manifest.m3u8",
  25,
  "",
  true,
  true,
  "",
  moment.utc(),
  new ContentControlledLiveSettings(
    { scalingFactor: 0 },
    5 * 1000 * 60,
    30 * 1000 * 60,
  ),
  [],
  [],
  [],
  false,
);
